import React, { useCallback, useEffect } from 'react'
import { FormikProps } from 'formik'
import {
  DatePickerV2,
  TextInputV4,
  labraUIDateFormat,
  TextAreaV4,
  BoxV4,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { debounce, get, isEmpty } from 'lodash'
import { useStyles } from './MicrosoftOfferDetail.styles'
import { OfferRecipientV3 } from '../../../common/components/OfferRecipientV3/OfferRecipientV3'
import { OfferRecipientWithTable } from '../../../common/components/OfferRecipientWithTable/OfferRecipientWithTable'
import clsx from 'clsx'
import { DateTime } from 'luxon'
import { toAcceptByDatePickerDate } from '../../../modules/privateOffer/privateOfferCreation/utils/microsoftPurchaseTermsUtil'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import { useSelector } from '../../../store'
import type {
  FieldMetadata,
  FieldMetadataField,
} from '../../../modules/privateOffer/types'
import type { Product } from '../../../modules/productsListing/types'
import type { OfferRecipients } from '../../../types/flyout'

export type MicrosoftOfferDetailProps = {
  formikProps: FormikProps<Record<string, unknown>>
  product: Product
  isSalesforceContext?: boolean
  offerDetailsFaqs?: JSX.Element
}

export const offerExpirationDateField = {
  title: 'Offer expiration date',
  humanReadableKey: 'Offer expiration date',
  placeHolder: 'Offer expiration date',
  id: 'acceptBy',
  isRequired: false,
  isReadOnly: false,
  isCustom: false,
  dataType: 'date',
  minDate: DateTime.now().plus({ days: 1 }).toISODate(),
}

export const MicrosoftOfferDetail: React.FC<MicrosoftOfferDetailProps> = ({
  formikProps,
  product,
  isSalesforceContext = false,
  offerDetailsFaqs,
}) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    submitCount,
  } = formikProps

  const fieldMetadata = useSelector(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[
        product.productId
      ]?.fieldMetadata
  ) as FieldMetadata | undefined

  const isOfferRecipientsFromTemplate =
    (
      (fieldMetadata?.offerRecipientsList as Record<string, unknown>)
        ?.offerRecipients as OfferRecipients[]
    )?.length > 0 || false

  const isCompanyNameReadOnly = (
    fieldMetadata?.companyName as FieldMetadataField
  )?.readOnly
  const isCompanyHidden = (fieldMetadata?.companyName as FieldMetadataField)
    ?.hidden
  const isAzureBillingAccountIdReadOnly = (
    fieldMetadata?.azureBillingAccountId as FieldMetadataField
  )?.readOnly
  const isAzureBillingAccountIdHidden = (
    fieldMetadata?.azureBillingAccountId as FieldMetadataField
  )?.hidden
  const isOfferRecipientsReadOnly = (
    fieldMetadata?.offerRecipients as FieldMetadataField
  )?.readOnly
  const isOfferRecipientsHidden = (
    fieldMetadata?.offerRecipients as FieldMetadataField
  )?.hidden
  const isCustomEmailMessageReadOnly = (
    fieldMetadata?.emailCustomMessage as FieldMetadataField
  )?.readOnly
  const isCustomEmailMessageHidden =
    (fieldMetadata?.emailCustomMessage as FieldMetadataField)?.hidden ?? false

  const isPrivateOfferNameReadOnly = (
    fieldMetadata?.privateOfferName as FieldMetadataField
  )?.readOnly
  const isPrivateOfferNameHidden = (
    fieldMetadata?.privateOfferName as FieldMetadataField
  )?.hidden

  const isOfferDescriptionReadOnly = (
    fieldMetadata?.offerDescription as FieldMetadataField
  )?.readOnly
  const isOfferDescriptionHidden = (
    fieldMetadata?.offerDescription as FieldMetadataField
  )?.hidden

  const isAcceptByReadOnly = (fieldMetadata?.acceptBy as FieldMetadataField)
    ?.readOnly
  const isAcceptByHidden = (fieldMetadata?.acceptBy as FieldMetadataField)
    ?.hidden

  const debouncedSetFieldValue = useCallback(
    debounce((name, value) => {
      setFieldValue(name, value)
    }, 500),
    []
  )

  const formChangeHandler = useCallback(
    (keyName: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        debouncedSetFieldValue(keyName, event.target.value)
      },
    [debouncedSetFieldValue]
  )
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()

  const acceptByPickerDate = toAcceptByDatePickerDate(
    values.acceptBy as string | null | undefined,
    timeZone
  )

  useEffect(() => {
    if (isEmpty(values.acceptBy) || !values.privateOfferId) {
      return
    }

    const normalized = toAcceptByDatePickerDate(
      String(values.acceptBy),
      timeZone
    )

    if (normalized && normalized !== values.acceptBy) {
      void setFieldValue('acceptBy', normalized)
    }
  }, [setFieldValue, timeZone, values.acceptBy, values.privateOfferId])

  const handleDateChange = useCallback(
    async (date: string | null) => {
      const updatedDate = date
        ? DateTime.fromISO(date, { zone: timeZone }).toFormat('yyyy-MM-dd')
        : ''
      await setFieldValue('acceptBy', updatedDate)
      await setFieldTouched('acceptBy', true)
    },
    [setFieldValue, setFieldTouched, timeZone]
  )
  return (
    <div data-testid="details-section">
      <BoxV4
        heading={'Offer details'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '16px', height: '16px' }}
          />
        }
        secondaryActions={offerDetailsFaqs}
      >
        <div className={classes.sectionContainer}>
          <div className={classes.companyDetailsContainer}>
            {!isCompanyHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="companyName"
                  title="Buyer name"
                  type="text"
                  placeHolder=""
                  value={values.companyName as string}
                  maxNumber={72}
                  isError={
                    !!(
                      get(touched, 'companyName', false) &&
                      get(errors, 'companyName', false)
                    )
                  }
                  isReadOnly={isCompanyNameReadOnly || false}
                  onChange={formChangeHandler('companyName')}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'companyName', false) &&
                      get(errors, 'companyName', '')) ||
                    ''
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
            {!isAzureBillingAccountIdHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="billingAccountId"
                  title="Buyer billing account ID"
                  type="text"
                  placeHolder=""
                  value={(values.billingAccountId || '') as string}
                  isError={
                    !!(
                      get(touched, 'billingAccountId', false) &&
                      get(errors, 'billingAccountId', false)
                    )
                  }
                  onChange={formChangeHandler('billingAccountId')}
                  isReadOnly={isAzureBillingAccountIdReadOnly || false}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'billingAccountId', false) &&
                      get(errors, 'billingAccountId', '')) ||
                    ''
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
          </div>
        </div>

        <div className={classes.companyDetailsContainer}>
          {!isPrivateOfferNameHidden && (
            <div className={classes.inputWidth}>
              <TextInputV4
                keyName="privateOfferName"
                title="Offer name"
                type="text"
                placeHolder=""
                value={values.privateOfferName as string}
                maxNumber={72}
                isError={
                  !!(
                    get(touched, 'privateOfferName', false) &&
                    get(errors, 'privateOfferName', false)
                  )
                }
                onChange={formChangeHandler('privateOfferName')}
                isReadOnly={isPrivateOfferNameReadOnly || false}
                isRequired={false}
                errorText={
                  (!!get(touched, 'privateOfferName', false) &&
                    get(errors, 'privateOfferName', '')) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
          )}
          <div
            className={clsx(
              classes.inputWidth,
              classes.datePicker,
              classes.datePickerErrorOutline
            )}
          >
            {!isAcceptByHidden && (
              <DatePickerV2
                views={['year', 'month', 'day']}
                size="small"
                format={labraUIDateFormat}
                field={{
                  ...offerExpirationDateField,
                  defaultValue: acceptByPickerDate ?? undefined,
                  isReadOnly: isAcceptByReadOnly || false,
                }}
                key={'acceptBy'}
                handleDateChange={handleDateChange}
                showErrorHelper={
                  !!touched.acceptBy && !isEmpty(errors.acceptBy)
                }
                fieldId={'acceptBy'}
                errorHelperText={(!!touched.acceptBy && errors.acceptBy) || ''}
                validate={true}
              />
            )}
          </div>
        </div>
        {isSalesforceContext && (
          <div className={classes.companyDetailsContainer}>
            <div className={classes.fullWidth}>
              <TextInputV4
                keyName="preparedBy"
                title="Prepared by (Email)"
                type="text"
                placeHolder=""
                value={values.preparedBy as string}
                maxNumber={72}
                isError={
                  !!(
                    get(touched, 'preparedBy', false) &&
                    get(errors, 'preparedBy', false)
                  )
                }
                isReadOnly={false}
                onChange={formChangeHandler('preparedBy')}
                isRequired={false}
                errorText={
                  (!!get(touched, 'preparedBy', false) &&
                    get(errors, 'preparedBy', '')) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                supportingText="Enter a valid email address."
              />
            </div>
          </div>
        )}
        <div className={classes.sectionContainer}>
          {!isOfferDescriptionHidden && (
            <TextAreaV4
              keyName="offerDescription"
              title="Offer description (Optional)"
              rows={10}
              maxNumber={1000}
              height={'80px'}
              type="string"
              placeHolder="Offer description (Optional)"
              value={(values.offerDescription || '') as string}
              isError={
                !!(
                  errors.offerDescription &&
                  (touched.offerDescription || submitCount > 0)
                )
              }
              onChange={formChangeHandler('offerDescription')}
              isReadOnly={isOfferDescriptionReadOnly || false}
              isRequired={false}
              errorText={
                ((touched.offerDescription || submitCount > 0) &&
                  errors.offerDescription) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          )}
        </div>
        {isOfferRecipientsFromTemplate ? (
          <>
            <OfferRecipientWithTable
              offerRecipients={
                (fieldMetadata?.offerRecipientsList as Record<string, unknown>)
                  ?.offerRecipients as OfferRecipients[]
              }
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
            />
          </>
        ) : (
          <>
            {!isOfferRecipientsHidden && (
              <div
                className={classes.sectionContainer}
                data-testid="recipients-details"
              >
                <div className={classes.textContainer}>
                  <Typography className={classes.sectionHeading}>
                    Offer recipients
                  </Typography>
                </div>
                <OfferRecipientV3
                  formik={
                    formikProps as unknown as FormikProps<
                      Record<string, unknown>
                    >
                  }
                  isReadOnly={isOfferRecipientsReadOnly}
                />
              </div>
            )}
          </>
        )}

        <div className={classes.sectionContainer}>
          {!isCustomEmailMessageHidden && (
            <TextAreaV4
              keyName="emailCustomMessage"
              title="Add custom email message to be sent to recipients"
              rows={10}
              maxNumber={1000}
              height="80px"
              type="string"
              placeHolder="Add custom email message to be sent to recipients"
              value={(values.emailCustomMessage || '') as string}
              isError={
                !!(errors.emailCustomMessage && touched.emailCustomMessage)
              }
              onChange={formChangeHandler('emailCustomMessage')}
              isReadOnly={isCustomEmailMessageReadOnly || false}
              isRequired={false}
              errorText={
                (!!touched.emailCustomMessage && errors.emailCustomMessage) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          )}
        </div>
      </BoxV4>
    </div>
  )
}
